import { lastChangedMs } from '../vault/trailer';
import { flat, kindOf, listOf, memberRefs, parseEntity, picksIn, refNames, sectionNamed, SOURCE_ROLE, TALK_ROLE } from '../entity/entity';
import type { MemberRef } from '../entity/entity';
import type { CatalogNote } from '../entity/catalog';
import { arcNeed, innerArcNames } from '../plan/arc';
import type { Arc } from '../plan/arc';
import { pinnedIndex } from './pinned';
import { readPlane } from '../plane/planeKey';
import type { PlanePlacement } from '../plane/planeKey';
import { isDeskContainer } from '../desk/deskContainer';

/**
 * A workspace, read off its file the way the app's `WorkspaceListing` reads
 * it: what it holds by reference, the plan it carries in order, the talk
 * beside them, and when it was started, last sat down to and put away.
 */
export interface Workspace {
  path: string;
  /** The id its front matter declares; null for a file without one. */
  id: string | null;
  /** The file's name, which is the workspace's name once it has one. */
  name: string;
  /** The name its file opens on — `# Name`, the way an arc carries its own.
   *  Null for a file written before nooks carried one. */
  title: string | null;
  /** False while the name is only the moment it was made. */
  named: boolean;
  started: string | null;
  sitting: string | null;
  /** Whether its file says `visibility: archived`. */
  archived: boolean;
  /** Whether its file says `visibility: inside`: held inside another nook, so on neither the desk,
   *  the shelf nor the list. */
  inside: boolean;
  sources: MemberRef[];
  talk: MemberRef | null;
  /** The arcs it holds, by filename, in the held order. */
  arcs: string[];
  /** What was placed on its plane — the list draws it in miniature. */
  plane: PlanePlacement[];
  /** Where it stands and what comes next, in its own words — its `glance:`
   *  line; null when it says nothing. */
  glance: string | null;
  /** Its own first step — its `start-with:` line; null when it says none. */
  startWith: string | null;
  /** Its tags, by name; the first is the colour it wears. */
  tags: string[];
  /** A scratch canvas: a nook laid out before it has a focus or a name. */
  scratch: boolean;
  /** Its `pinned:` index (see [pinned.ts]); null while it is not pinned. */
  pinned: number | null;
  /** A draft: a nook the suggestions made that nobody has kept yet (see [isDraftNook]). */
  draft: boolean;
  /** Its face: the lines picked inside it, in the order they were picked. */
  face: string[];
  /** Sources gathered into it and not yet kept, as `path` or `path#^anchor`. */
  unconfirmed: string[];
  /** When it last changed, as its file says (see [lastChangedMs]); its `sitting` day where the file
   *  says nothing finer. */
  updatedMs: number | null;
}

export const SCRATCH_NAME = 'Scratch canvas';

/** Whether a nook's front matter marks it as a scratch canvas. */
export function isScratch(frontMatter: Record<string, string | string[]>): boolean {
  return flat(frontMatter['scratch']).trim().toLowerCase() === 'true';
}

/** [raw] without its `scratch:` marker: from then on an ordinary nook. */
export function withoutScratch(raw: string): string {
  const front = raw.match(FRONT_MATTER);
  if (!front) return raw;
  return front[0].replace(/^scratch:.*\n/m, '') + raw.slice(front[0].length);
}

const DRAFT_LINE = /^draft:[ \t]*(.*?)[ \t]*$/m;

/** Whether [raw]'s front matter marks its nook as a draft — `draft: <day it was suggested>`. The
 *  app writes and reads the same line; taking it out is what keeps the nook. */
export function isDraftNook(raw: string): boolean {
  const front = raw.match(FRONT_MATTER);
  return !!front && DRAFT_LINE.test(front[0]);
}

/** [raw] without its `draft:` line: a kept nook. Everything else survives byte for byte. */
export function withoutDraft(raw: string): string {
  const front = raw.match(FRONT_MATTER);
  if (!front) return raw;
  const kept = front[0].replace(/^draft:.*\n/gm, '');
  return kept === front[0] ? raw : kept + raw.slice(front[0].length);
}

/** [change] as a person's act on a nook: whatever it changes keeps a draft, in the same write. */
export function keeping(change: (raw: string) => string): (raw: string) => string {
  return (raw) => {
    const next = change(raw);
    return next === raw ? raw : withoutDraft(next);
  };
}

/** "Workspace" is the older label: a file keeps the path it was first written at. */
const UNNAMED = /^(?:Nook|Space|Workspace) \d{4}-\d{2}-\d{2} at \d{2}-\d{2}(?: \(\w+\))?$/;

/** True for a name that is only the minute the nook was made. */
export function isUnnamed(name: string): boolean {
  return UNNAMED.test(name);
}

const FRONT_MATTER = /^---\n[\s\S]*?\n---\n/;
const TITLE_LINE = /^#[ \t]+(.+?)[ \t]*$/;

/**
 * The name a nook's file opens on: a `# ` line first in its body. Only there —
 * further down, a heading is the nook's own writing, not its name. `# Face` is
 * structure, not a name.
 */
export function nookTitle(raw: string): string | null {
  const front = raw.match(FRONT_MATTER);
  const body = raw.slice(front ? front[0].length : 0).replace(/^\s+/, '');
  const title = body.split('\n', 1)[0]?.match(TITLE_LINE)?.[1]?.trim();
  return title && title !== 'Face' ? title : null;
}

/**
 * A nook renamed: its opening title line rewritten, or written above
 * everything else where it has none. Everything below survives byte for
 * byte. A blank name removes the opening title line and nothing else; the name
 * it already has changes nothing. Naming the scratch nook is what keeps it: the
 * marker goes with the same write.
 */
export function withNookName(raw: string, name: string): string {
  const wanted = name.replace(/\s+/g, ' ').trim();
  const titled = nookTitle(raw) !== null;
  if (wanted === (nookTitle(raw) ?? '')) return raw;
  const front = raw.match(FRONT_MATTER);
  const head = front ? front[0] : '';
  const body = raw.slice(head.length).replace(/^\n+/, '');
  if (!wanted) {
    const rest = body.slice(body.indexOf('\n') + 1).replace(/^\n+/, '');
    return rest ? `${head}\n${rest}` : head;
  }
  const rest = !titled ? body : body.slice(body.indexOf('\n') + 1).replace(/^\n+/, '');
  return withoutScratch(`${head}\n# ${wanted}\n${rest ? `\n${rest}` : ''}`);
}

/** The workspace [raw] describes, or null when the file is not one. The desk's container is none,
 *  wherever it is and whatever its marker says: it holds the desk, and is no nook of its own. */
export function parseWorkspace(path: string, raw: string): Workspace | null {
  const entity = parseEntity(path, raw);
  if (kindOf(entity) !== 'workspace' || isDeskContainer(raw)) return null;
  const fm = entity.frontMatter;
  const refs = memberRefs(raw);
  const name = entity.name.replace(/\.md$/, '');
  return {
    path,
    id: flat(fm['id']) || null,
    name,
    title: nookTitle(raw),
    named: !UNNAMED.test(name),
    // `opened:` is the older key, carried by a file until the app next exports it.
    started: flat(fm['started']) || flat(fm['opened']) || null,
    sitting: flat(fm['sitting']) || null,
    archived: flat(fm['visibility']).trim() === 'archived',
    inside: flat(fm['visibility']).trim() === 'inside',
    sources: refs.filter((ref) => ref.role === SOURCE_ROLE),
    talk: refs.find((ref) => ref.role === TALK_ROLE) ?? null,
    arcs: refNames(fm['arcs']),
    plane: readPlane(entity),
    glance: flat(fm['glance']).trim() || null,
    startWith: flat(fm['start-with']).trim() || null,
    tags: listOf(fm['tags']),
    scratch: isScratch(fm),
    pinned: pinnedIndex(fm),
    draft: isDraftNook(raw),
    face: picksIn(sectionNamed(entity, 'face')?.body ?? '')
      .map((pick) => pick.text)
      .filter((text) => !text.trimStart().startsWith('{"')),
    unconfirmed: listOf(fm['unconfirmed']),
    updatedMs: lastChangedMs(raw) ?? dayMs(flat(fm['sitting']) || flat(fm['started']) || flat(fm['opened'])),
  };
}

function dayMs(day: string): number | null {
  const ms = day ? Date.parse(day) : NaN;
  return Number.isNaN(ms) ? null : ms;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "3 Oct" for a `YYYY-MM-DD` day; null for anything else. */
function shortDay(day: string | null): string | null {
  const m = day ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(day) : null;
  return m ? `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}` : null;
}

/**
 * What this workspace is called now, or no name at all.
 *
 * The file's own title line: the app writes one for every named nook, so a
 * file without one is unnamed, whatever a lagging catalog row still says (a
 * name cleared on the web would otherwise come back). A hand-made file with no
 * title falls back to its filename, unless that is only the minute it was
 * made. A nook nobody named has an empty name: each surface decides how to
 * show one, never by inventing a name from its filename.
 */
export function nameOf(
  w: Workspace,
  _catalog: ReadonlyMap<string, CatalogNote>,
): { name: string; named: boolean } {
  if (w.scratch) {
    const day = shortDay(w.started);
    return { name: day ? `${SCRATCH_NAME} · ${day}` : SCRATCH_NAME, named: true };
  }
  const given = (name: string | null | undefined) =>
    name && !UNNAMED.test(name) ? { name, named: true } : { name: '', named: false };
  return given(w.title ?? w.name);
}

/** Live ones first, most recently sat down to at the top; the archived ones
 *  after them, most recently archived first. Scratch canvases are in neither:
 *  they have a list of their own ([scratchCanvases]). Nor are drafts: a live
 *  one waits on the desk ([draftNooks]), and one let go is gone.
 *  Nor is a nook held inside another: it is that nook's. */
export function arrange(workspaces: Workspace[]): { live: Workspace[]; away: Workspace[] } {
  const newestFirst = (key: (w: Workspace) => string) => (a: Workspace, b: Workspace) =>
    key(b).localeCompare(key(a));
  const listed = workspaces.filter((w) => !w.scratch && !w.draft && !w.inside);
  return {
    live: listed.filter((w) => !w.archived).sort(newestFirst((w) => w.sitting ?? w.started ?? '')),
    away: listed.filter((w) => w.archived).sort(newestFirst((w) => w.sitting ?? w.started ?? '')),
  };
}

/** The drafts nobody has kept or let go yet, the most recently changed first; one held inside
 *  another nook is that nook's. */
export function draftNooks(workspaces: readonly Workspace[]): Workspace[] {
  return workspaces
    .filter((w) => w.draft && !w.archived && !w.inside && !w.scratch)
    .sort(
      (a, b) =>
        (b.updatedMs ?? 0) - (a.updatedMs ?? 0) ||
        (b.sitting ?? b.started ?? '').localeCompare(a.sitting ?? a.started ?? '') ||
        a.path.localeCompare(b.path),
    );
}

/** The arcs a nook holds, in its order, archived ones left out. */
export function heldArcsOf(w: Workspace, arcsByName: ReadonlyMap<string, Arc>): Arc[] {
  return w.arcs.map((name) => arcsByName.get(name)).filter((arc): arc is Arc => !!arc && !arc.archived);
}

/** The way in: the first arc of a nook's plan that is neither done nor
 *  parked. Only the plan itself — a storyline's members are its own. */
export function wayIn(w: Workspace, arcsByName: ReadonlyMap<string, Arc> | null): Arc | null {
  if (!arcsByName) return null;
  return heldArcsOf(w, arcsByName).find((arc) => arc.status !== 'done' && arc.status !== 'parked') ?? null;
}

/**
 * The first step a nook shows under its glance: its own, else the one of the
 * first held arc that is open and not parked — and only when that arc's
 * status shows a first step. [held] is the nook's arcs in its order; null
 * while they are unknown.
 */
export function startWithOf(own: string | null, held: readonly Arc[] | null): string | null {
  if (own) return own;
  const first = held?.find((arc) => arc.status !== 'done' && arc.status !== 'parked' && !arc.archived);
  if (!first) return null;
  const need = arcNeed(first);
  return need && !need.asking && need.text ? need.text : null;
}

/**
 * How many arcs wait on you in a nook: every distinct needs-input arc it
 * reaches — its plan, and the members of any storyline in it at any depth,
 * the storyline itself included. An arc reached twice counts once. Archived
 * arcs, and whatever is reached only through one, are left out.
 */
export function waitingOnYou(w: Workspace, arcsByName: ReadonlyMap<string, Arc> | null): number {
  if (!arcsByName) return 0;
  const seen = new Set<string>();
  let waiting = 0;
  const reach = (name: string) => {
    if (seen.has(name)) return;
    seen.add(name);
    const arc = arcsByName.get(name);
    if (!arc || arc.archived) return;
    if (arc.status === 'needs-input') waiting++;
    innerArcNames(arc).forEach(reach);
  };
  w.arcs.forEach(reach);
  return waiting;
}

/**
 * Where a nook made on the web goes: `spaces/Nook <local time>.md`, the
 * shape the app gives an unnamed one. [taken] guards the minute two nooks
 * share.
 */
export function newNookPath(at: Date, taken: (path: string) => boolean = () => false): string {
  const two = (n: number) => String(n).padStart(2, '0');
  const stamp =
    `${at.getFullYear()}-${two(at.getMonth() + 1)}-${two(at.getDate())}` +
    ` at ${two(at.getHours())}-${two(at.getMinutes())}`;
  const base = `spaces/Nook ${stamp}`;
  let candidate = `${base}.md`;
  for (let n = 2; n < 100 && taken(candidate); n++) candidate = `${base} (${n}).md`;
  return candidate;
}

/**
 * A nook's first file: only keys the app owns, so nothing is carried twice.
 * The importer mints a workspace from `kind: workspace` and takes the `id:`
 * it declares; its name is the filename until someone gives it one.
 */
export function newNookFile(id: string, at: Date): string {
  const two = (n: number) => String(n).padStart(2, '0');
  const day = `${at.getFullYear()}-${two(at.getMonth() + 1)}-${two(at.getDate())}`;
  return `---\nid: ${id}\nkind: workspace\nstarted: ${day}\nsitting: ${day}\n---\n`;
}
